import React, { useEffect, useState } from "react";
import Briciole from "@/components/Briciole";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Archive, RotateCcw, FileText, Wrench, ExternalLink, Truck, Trash2 } from "lucide-react";
import { fileUrl } from "@/lib/placeholders";
import { useAuth } from "@/context/AuthContext";
import { eAmministratore } from "@/lib/ruoli";

export default function ArchivioAttrezzature() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openEq, setOpenEq] = useState(null);
  const { user } = useAuth();
  // La cancellazione definitiva e' dell'amministratore, come per le persone.
  const puoCancellare = eAmministratore(user);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/equipment/archived");
      setList(data);
    } catch { toast.error("Errore caricamento archivio"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (eq) => {
    if (!window.confirm(`Ripristinare ${eq.identifier} tra le attrezzature attive?`)) return;
    try {
      await api.post(`/equipment/${eq.id}/restore`);
      toast.success("Attrezzatura ripristinata");
      load();
    } catch { toast.error("Errore"); }
  };

  const elimina = async (eq) => {
    const docs = eq.documents_count || 0;
    const manut = eq.maintenance_count || 0;
    if (!window.confirm(
      `ELIMINARE DEFINITIVAMENTE ${eq.identifier}?\n\n` +
      `Vengono cancellati la scheda, la foto, ${docs} document${docs === 1 ? "o" : "i"} ` +
      `e ${manut} registrazion${manut === 1 ? "e" : "i"} di manutenzione, con i costi relativi.\n\n` +
      `L'operazione non è reversibile.`
    )) return;
    if (!window.confirm(`Conferma definitiva: elimino ${eq.identifier} e il suo storico.`)) return;
    try {
      const { data } = await api.delete(`/equipment/${eq.id}/permanent`);
      toast.success(`${eq.identifier} eliminata · ${data.deleted_documents} documenti, ${data.deleted_logs} manutenzioni`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const cats = Array.from(new Set(list.map(e => e.category))).filter(Boolean).sort();
  const q = filter.trim().toLowerCase();
  const filtered = list
    .filter(e => catFilter === "all" || e.category === catFilter)
    .filter(e => !q || [e.identifier, e.brand, e.model].some(v => (v || "").toLowerCase().includes(q)));

  return (
    <div className="space-y-6" data-testid="archivio-attrezzature-page">
      <Briciole voci={[
        { testo: "Mezzi e attrezzature", a: "/attrezzature" },
        { testo: "Archivio dismesse" },
      ]} />

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Mezzi e attrezzature</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Archivio dismesse</h1>
        <p className="text-sm text-slate-500 mt-1">
          {list.length} mezzi/strumenti dismessi · documenti e manutenzioni conservati integralmente
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca per targa/matricola, marca, modello..." className="pl-9 h-11"
                 value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        <select className="h-11 border border-slate-200 rounded px-3 text-sm min-w-[200px]"
                value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
          <option value="all">Tutte le categorie ({list.length})</option>
          {cats.map(c => (
            <option key={c} value={c}>{c} ({list.filter(e => e.category === c).length})</option>
          ))}
        </select>
      </div>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!loading && filtered.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="p-10 text-center">
            <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-slate-700">
              {list.length === 0 ? "Archivio vuoto" : "Nessun risultato per questi filtri"}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Le attrezzature dismesse compariranno qui, con lo storico documenti e manutenzioni conservato.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(eq => (
          <Card key={eq.id} className="border-slate-200 bg-slate-50/60">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <Truck className="w-6 h-6 text-slate-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{eq.category}</div>
                  <div className="font-display font-bold text-lg leading-tight truncate">
                    {[eq.brand, eq.model].filter(Boolean).join(" ") || eq.identifier}
                  </div>
                  <div className="text-xs font-mono text-slate-600">{eq.identifier}</div>
                </div>
              </div>

              <div className="text-xs text-slate-500 mt-3 space-y-0.5">
                {eq.archived_at && <div>Dismessa il {eq.archived_at.substring(0, 10)}</div>}
                {eq.archived_by && <div className="truncate">da {eq.archived_by}</div>}
              </div>

              <div className="flex gap-2 mt-3">
                <Badge variant="outline" className="text-[10px]">
                  <FileText className="w-3 h-3 mr-1" />{eq.documents_count ?? 0} documenti
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  <Wrench className="w-3 h-3 mr-1" />{eq.maintenance_count ?? 0} interventi
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setOpenEq(eq)} data-testid={`dossier-eq-${eq.id}`}>
                  <FileText className="w-3 h-3 mr-1" /> Consulta fascicolo
                </Button>
                <Button variant="outline" size="sm" onClick={() => restore(eq)}
                        className="text-emerald-700 hover:bg-emerald-50">
                  <RotateCcw className="w-3 h-3 mr-1" /> Ripristina
                </Button>
                {puoCancellare && (
                  <Button variant="outline" size="sm" onClick={() => elimina(eq)}
                          className="text-red-600 hover:bg-red-50" title="Elimina definitivamente"
                          data-testid={`elimina-eq-${eq.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {openEq && <DossierDialog eq={openEq} onClose={() => setOpenEq(null)} />}
    </div>
  );
}

function DossierDialog({ eq, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/equipment/${eq.id}/dossier`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Errore caricamento fascicolo"));
  }, [eq.id]);

  const docs = data?.documents || [];
  const logs = data?.maintenance_logs || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Fascicolo dismesso · {eq.identifier}
            <Badge variant="outline" className="font-normal">{eq.category}</Badge>
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
          Sola consultazione. Documenti e interventi restano conservati per tracciabilità;
          per modificarli occorre prima ripristinare l'attrezzatura.
        </p>

        {!data && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

        {data && (
          <div className="space-y-5">
            <div>
              <h3 className="font-display font-bold mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-500" /> Documenti ({docs.length})
              </h3>
              {docs.length === 0
                ? <p className="text-sm text-slate-500">Nessun documento</p>
                : <div className="space-y-2">
                    {docs.map(d => (
                      <div key={d.id} className="p-2 rounded border border-slate-200 flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{d.title}</div>
                          <div className="text-xs text-slate-500">
                            {d.doc_type}{d.expires_date && ` · scad. ${d.expires_date.substring(0, 10)}`}
                          </div>
                        </div>
                        {d.file_url && (
                          <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>}
            </div>

            <div>
              <h3 className="font-display font-bold mb-2 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-orange-500" /> Interventi manutenzione ({logs.length})
              </h3>
              {logs.length === 0
                ? <p className="text-sm text-slate-500">Nessun intervento registrato</p>
                : <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[480px]">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                        <tr>
                          <th className="text-left p-2">Data</th>
                          <th className="text-left p-2">Tipo</th>
                          <th className="text-left p-2">Descrizione</th>
                          <th className="text-right p-2">Costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map(l => (
                          <tr key={l.id} className="border-t border-slate-100">
                            <td className="p-2 whitespace-nowrap">{l.date?.substring(0, 10)}</td>
                            <td className="p-2"><Badge variant="outline" className="text-[10px]">{l.type}</Badge></td>
                            <td className="p-2">{l.description}</td>
                            <td className="p-2 text-right">{l.cost ? `€ ${l.cost.toFixed(2)}` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
